import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { verifyPayment } from '@/api/payments/paymentsService'
import { TranzilaHostedFields } from '@/components/TranzilaHostedFields'
import { Card, CardContent } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useToast } from '@/hooks/useToast'
import { logWarn } from '@/lib/sentryLogs'
import { RoutePath } from '@/RoutePath'

import { PaymentLinkResponse } from './components/PaymentData'

const VERIFY_POLL_INTERVAL_MS = 2000
const VERIFY_MAX_ATTEMPTS = 8

interface Props {
  paymentLinkData: PaymentLinkResponse | null
  pkgId: string
  onCompletePaymentSuccess: () => void
}

export default function ProcessPage({ paymentLinkData, pkgId, onCompletePaymentSuccess }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showErrorToast } = useToast()
  const [isVerifying, setIsVerifying] = useState(false)

  const onCompleteRef = useRef(onCompletePaymentSuccess)
  onCompleteRef.current = onCompletePaymentSuccess

  // Poll until the webhook marks the package as paid. Chaining setTimeout keeps
  // a single request in flight at a time (so success can only fire once), and
  // the `cancelled` flag from the effect cleanup stops polling when the user
  // leaves or the package changes — a stale poll can never resolve the success
  // dialog for a previous package.
  useEffect(() => {
    if (!isVerifying) return

    let cancelled = false
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const poll = async () => {
      attempts++
      try {
        const { isPaid } = await verifyPayment(pkgId)
        if (cancelled) return
        if (isPaid) {
          onCompleteRef.current()
          return
        }
      } catch {
        if (cancelled) return
      }

      if (attempts >= VERIFY_MAX_ATTEMPTS) {
        logWarn('Payment verification timed out', { pkgId, attempts })
        onCompleteRef.current()
        navigate(RoutePath.ClientPkg)
        return
      }

      timer = setTimeout(poll, VERIFY_POLL_INTERVAL_MS)
    }

    poll()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [isVerifying, pkgId, navigate])

  useEffect(() => {
    if (!paymentLinkData) {
      navigate(`${RoutePath.PaymentSummary}/${pkgId}`)
    }
  }, [paymentLinkData, pkgId, navigate])

  const handleChargeSuccess = useCallback(() => {
    setIsVerifying(true)
  }, [])

  const handleChargeError = useCallback(
    (error: string) => {
      showErrorToast(t('payment.chargeError'), error)
    },
    [showErrorToast, t]
  )

  if (!paymentLinkData) return null

  return (
    <div className='space-y-4'>
      {!isVerifying && (
        <TranzilaHostedFields paymentLinkData={paymentLinkData} onSuccess={handleChargeSuccess} onError={handleChargeError} />
      )}

      {isVerifying && (
        <Card>
          <CardContent className='flex flex-col items-center gap-6 py-12'>
            <Spinner className='size-8' />
            <p className='text-text-primary text-sm font-medium'>{t('payment.verifyingPayment')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
